// The section configs behind the two export drawers.
//
// COMPANY_EXPORT_SECTIONS is the SQL settings drawer's four, in render order —
// the UI set, deliberately NOT the contract set. The company-export endpoints
// accept five types (COMPANY_MANAGED_EXPORT_TYPES); the fifth,
// form_structure_export, is managed the same way but belongs to the Form
// structure tab, so listing it here would draw it as a stray fifth section.
// Keeping the two lists apart is what stops that — it gets its own one-element
// list below (FORM_STRUCTURE_SECTION) and the SAME drawer component renders it.
//
// One literal drives everything about a section — order, label, icon and table
// name — so adding one later is a row here plus two i18n keys.
//
// Table names are never typed and never come from the server on a NEW export:
// the backend names these by convention and this reproduces it, which is why
// the drawer's field is a locked display. A SAVED export carries its own
// `tableName`, and that is what gets rendered once one exists.

import { SqlExportType, type CompanyManagedExportType } from '@/api/endpoints/dataSetupApi';

export interface CompanyExportSection {
  type: CompanyManagedExportType;
  /** i18n key for the section heading. */
  labelKey: string;
  /** Kebab-case Icon name — all four verified against the installed lucide. */
  icon: string;
  /** Table name before the company code is appended. */
  tablePrefix: string;
}

export const COMPANY_EXPORT_SECTIONS: CompanyExportSection[] = [
  {
    type: SqlExportType.Users,
    labelKey: 'data_setup_sql_users',
    icon: 'users',
    tablePrefix: 'company_users',
  },
  {
    type: SqlExportType.Tasks,
    labelKey: 'data_setup_sql_tasks',
    icon: 'square-check-big',
    tablePrefix: 'company_tasks',
  },
  {
    type: SqlExportType.Attendance,
    labelKey: 'data_setup_sql_attendance',
    icon: 'calendar-check',
    tablePrefix: 'company_attendance',
  },
  {
    // The wire member and the table convention disagree here — `company_data_export`
    // names a `company_submissions_*` table. Confirmed against a real
    // exports/list row (`company_submissions_10000001`).
    type: SqlExportType.CompanyData,
    labelKey: 'data_setup_sql_company',
    icon: 'network',
    tablePrefix: 'company_submissions',
  },
];

/** The Form structure tab's own section — that fifth managed type, rendered by
 *  its own ExportSettingsDrawer rather than appearing among the four above.
 *
 *  NOTE the prefix breaks the shape of the other four: no `company_` and no
 *  internal underscore, so the name comes out `formstructure_58914356`. That is
 *  what the design specifies. Unlike company_submissions — checked against a
 *  real exports/list row — this one has NOT been confirmed against one yet. */
export const FORM_STRUCTURE_SECTION: CompanyExportSection = {
  type: SqlExportType.FormStructure,
  labelKey: 'data_setup_structure_export_title',
  icon: 'table-2',
  tablePrefix: 'formstructure',
};

/** Power BI's table, which has no section behind it but follows the same rule. */
export const POWER_BI_TABLE_PREFIX = 'power_bi_function';

/** `<prefix>_<companyCode>`, e.g. `company_users_58914356`. Empty when the
 *  company code is unknown — callers render an explicit unavailable state,
 *  which beats writing `company_users_undefined` into the warehouse. */
export const companyTableName = (prefix: string, companyCode: string | undefined) =>
  companyCode ? `${prefix}_${companyCode}` : '';

export const tableNameFor = (section: CompanyExportSection, companyCode: string | undefined) =>
  companyTableName(section.tablePrefix, companyCode);
